import type { Root, SubcategorySettings } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { boardCardFields } from "../tasks/board-configuration.ts";
import { cloneTaskComments } from "../tasks/task-duplication-controller.ts";
import { approvalApplies } from "../tasks/task-approvals.ts";
import { normalizeSortDirection, normalizeTaskSort } from "../tasks/selectors.ts";
import type { OperationProgress } from "../../ui/operation-progress.ts";

export interface CollectionManagementCapabilities {
  roots(): Root[];
  categories(): Category[];
  tasks(): Todo[];
  editable(): boolean;
  activeCategory(): string | null;
  clearActiveCategory(): void;
  dialog(mode: "create" | "edit", rootName: string, category?: Category, rootId?: string): Promise<SubcategorySettings | null>;
  confirmDelete(category: Category): Promise<boolean>;
  duplicateOptions(category: Category, taskCount: number): Promise<boolean | null>;
  create(id: string, rootId: string, name: string, settings: SubcategorySettings): Promise<void>;
  change(id: string, rootId: string, update: (category: Category) => void): Promise<void>;
  createTask(id: string, rootId: string, update: (task: Todo) => void): Promise<void>;
  copyAttachments(sourceId: string, destinationId: string): Promise<{ copied: number; failed: number }>;
  online(): boolean;
  randomId(): string;
  markLocal(): void;
  render(): Promise<void>;
  toast(message: string, error?: boolean): void;
  sync(): void | Promise<void>;
  now(): number;
  progress?(value: OperationProgress | null): void;
}

function applySettings(category: Category, settings: SubcategorySettings): void {
  if (settings.customFieldIds) category.customFieldIds = settings.customFieldIds;
  if (settings.customFieldSchema) category.customFieldSchema = settings.customFieldSchema;
  category.name = settings.name; category.viewType = settings.viewType; category.boardColumns = settings.boardColumns;
  category.boardWipLimits = settings.boardWipLimits; category.boardColumnSettings = settings.boardColumnSettings; category.boardCardFields = settings.boardCardFields; category.boardAutomation = settings.boardAutomation;
  category.approvalRule = settings.approvalRule;
  category.sortBy = settings.sortBy; category.sortDirection = settings.sortDirection; category.pageSize = settings.pageSize; category.showCompleted = settings.showCompleted;
  category.defaultPriority = settings.defaultPriority; category.defaultTags = settings.defaultTags; category.defaultAssigneeIds = settings.defaultAssigneeIds; category.defaultTaskType = settings.defaultTaskType; category.defaultTemplateId = settings.defaultTemplateId; category.defaultDueOffsetDays = settings.defaultDueOffsetDays;
}

export function createCollectionManagementController(capabilities: CollectionManagementCapabilities) {
  let duplicating = false;
  const finish = async (message: string) => { capabilities.markLocal(); await capabilities.render(); capabilities.toast(message); void capabilities.sync(); };
  return {
    async add(rootId: string): Promise<void> {
      const root = capabilities.roots().find(item => item.id === rootId); if (!root) return;
      const settings = await capabilities.dialog("create", root.name, undefined, root.id); if (!settings) return;
      await capabilities.create(capabilities.randomId(), rootId, settings.name, settings); capabilities.markLocal(); await capabilities.render(); capabilities.sync();
    },
    async edit(id: string): Promise<void> {
      const category = capabilities.categories().find(item => item.id === id); if (!category) return;
      const root = capabilities.roots().find(item => item.id === category.rootId);
      const settings = await capabilities.dialog("edit", root?.name || "Unknown category", category); if (!settings) return;
      await capabilities.change(id, category.rootId, item => applySettings(item, settings)); await finish("Collection updated");
    },
    async delete(id: string): Promise<void> {
      const category = capabilities.categories().find(item => item.id === id); if (!category || !await capabilities.confirmDelete(category)) return;
      await capabilities.change(id, category.rootId, item => { item.deleted = true; });
      if (capabilities.activeCategory() === id) capabilities.clearActiveCategory();
      await finish("Collection deleted");
    },
    async duplicate(id: string): Promise<void> {
      if (duplicating) return;
      duplicating = true;
      try {
      const source = capabilities.categories().find(item => item.id === id); if (!source || !capabilities.editable()) return;
      const tasks = capabilities.tasks().filter(task => task.categoryId === id && !task.deleted).map(task => structuredClone(task));
      const includeTasks = await capabilities.duplicateOptions(source, tasks.length);
      if (includeTasks === null) return;
      const settings: SubcategorySettings = {
        customFieldIds: structuredClone(source.customFieldIds || []), customFieldSchema: structuredClone(source.customFieldSchema || {}),
        name: `${source.name} copy`, viewType: source.viewType || "list", boardColumns: source.boardColumns || [], boardWipLimits: source.boardWipLimits || {}, boardColumnSettings: source.boardColumnSettings || {}, boardCardFields: boardCardFields(source.boardCardFields), boardAutomation: source.boardAutomation || { completedColumn: null, overdueUrgent: false, completeWithSubtasks: false }, approvalRule: source.approvalRule || null,
        sortBy: normalizeTaskSort(source.sortBy), sortDirection: normalizeSortDirection(source.sortDirection, source.sortBy), pageSize: source.pageSize || 0, showCompleted: !!source.showCompleted,
        defaultPriority: source.defaultPriority || "none", defaultTags: source.defaultTags || [], defaultAssigneeIds: source.defaultAssigneeIds || [], defaultTaskType: source.defaultTaskType || "text", defaultTemplateId: source.defaultTemplateId || null, defaultDueOffsetDays: source.defaultDueOffsetDays ?? null,
      };
      const collectionId = capabilities.randomId();
      const taskIds = new Map(includeTasks ? tasks.map(task => [task.id, capabilities.randomId()] as const) : []);
      let created = 0;
      let collectionCreated = false;
      try {
        capabilities.progress?.({ stage: `Copying collection “${source.name}”` });
        await capabilities.create(collectionId, source.rootId, settings.name, settings);
        collectionCreated = true;
        if (source.referenceNote) await capabilities.change(collectionId, source.rootId, draft => { draft.referenceNote = source.referenceNote; draft.referenceAnchors = structuredClone(source.referenceAnchors || []); });
        if (includeTasks) capabilities.progress?.({ stage: "Copying tasks", completed: 0, total: tasks.length });
        if (includeTasks) for (const task of tasks) {
          const taskId = taskIds.get(task.id)!;
          await capabilities.createTask(taskId, source.rootId, draft => {
            const createdAt = draft.createdAt;
            Object.assign(draft, structuredClone(task), {
              id: taskId, rootId: source.rootId, categoryId: collectionId, createdAt, deleted: false,
              subtasks: (task.subtasks || []).map(item => ({ ...item, id: capabilities.randomId() })),
              comments: cloneTaskComments(task.comments || [], capabilities.randomId),
              reminders: (task.reminders || []).map(item => ({ ...item, id: capabilities.randomId() })),
              dependencyIds: (task.dependencyIds || []).map(link => taskIds.get(link) || link),
              relationships: (task.relationships || []).map(link => ({ ...link, taskId: taskIds.get(link.taskId) || link.taskId })),
              recurrenceSeriesId: task.recurrence ? taskId : null, recurrenceOccurrence: 0,
              externalCalendars: [], automationEvents: [], approvalEvents: [],
            });
            if (approvalApplies({ ...source, id: collectionId }, draft)) { draft.completed = false; draft.completedAt = null; }
          });
          created++;
          capabilities.progress?.({ stage: "Copying tasks", completed: created, total: tasks.length });
        }
      } catch (error) {
        if (collectionCreated) { capabilities.markLocal(); try { await capabilities.render(); } catch { /* The partial-copy warning must survive a failed redraw. */ } void capabilities.sync(); }
        capabilities.toast(collectionCreated ? `Collection copy is incomplete (${created}/${tasks.length} tasks). ${error instanceof Error ? error.message : "Please check local storage."}` : `Could not duplicate collection: ${error instanceof Error ? error.message : "Please check local storage."}`, true);
        return;
      }
      capabilities.markLocal();
      try { await capabilities.render(); }
      catch { capabilities.toast("Collection copy saved locally, but the page could not refresh. Reload to check it; do not duplicate the collection again.", true); void capabilities.sync(); return; }
      capabilities.toast(includeTasks ? `Collection duplicated with ${created} ${created === 1 ? "task" : "tasks"}` : "Collection duplicated");
      capabilities.progress?.({ stage: "Synchronizing collection copy" });
      try { await capabilities.sync(); }
      catch { capabilities.toast("Collection saved locally, but sync needs attention. Attachments were not copied.", true); return; }
      if (includeTasks && created && capabilities.online()) {
        let copied = 0; let failed = 0; let checked = 0;
        capabilities.progress?.({ stage: "Checking copied task files", completed: 0, total: tasks.length });
        for (const task of tasks) {
          try { const result = await capabilities.copyAttachments(task.id, taskIds.get(task.id)!); copied += result.copied; failed += result.failed; }
          catch { failed++; }
          capabilities.progress?.({ stage: "Checking copied task files", completed: ++checked, total: tasks.length });
        }
        if (copied || failed) capabilities.toast(`${copied} ${copied === 1 ? "attachment" : "attachments"} copied${failed ? ` · ${failed} could not be copied` : ""}`, failed > 0);
      } else if (includeTasks && created && !capabilities.online()) capabilities.toast("Attachments were not copied while offline; tasks were saved locally", true);
      } finally { duplicating = false; capabilities.progress?.(null); }
    },
    async archive(id: string): Promise<void> {
      const category = capabilities.categories().find(item => item.id === id); if (!category) return;
      const restoring = !!category.archivedAt;
      await capabilities.change(id, category.rootId, draft => { draft.archivedAt = restoring ? null : capabilities.now(); });
      if (capabilities.activeCategory() === id) capabilities.clearActiveCategory();
      await finish(restoring ? "Collection restored" : "Collection archived");
    },
  };
}
